/**
 * Red/green/blue color.
 */
export interface Rgb {
  /**
   * Red channel.
   */
  r: number
  /**
   * Green channel.
   */
  g: number
  /**
   * Blue channel.
   */
  b: number
}

/**
 * Darkens the specified color.
 *
 * @param color
 * @param percentage
 */
export function darken(color: string | Rgb, percentage: number) {
  const colorRgb = typeof color === 'string' ? hexToRgb(color) : color
  const blackRgb = { r: 0, g: 0, b: 0 }
  const weight = percentage / 100

  const mixedRgb = colorMix(blackRgb, colorRgb, weight)
  return rgbToHex(mixedRgb)
}

/**
 * Mixes 2 colors.
 *
 * @param color1 The first color.
 * @param color2 The second color.
 * @param weight The weight - 0.5 means half-and-half, 1 means
 * color 1 fully, 0 means color 2 fully.
 */
export function colorMix(color1: Rgb, color2: Rgb, weight: number) {
  const r = Math.round(color1.r * weight + color2.r * (1 - weight))

  const g = Math.round(color1.g * weight + color2.g * (1 - weight))
  const b = Math.round(color1.b * weight + color2.b * (1 - weight))

  return { r, g, b }
}

/**
 * Converts a hex color to RGB.
 *
 * @param hex
 */
export function hexToRgb(hex: string): Rgb {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, '')

  // Expand 3-digit hex to 6-digit
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('')
  }

  // Parse the r, g, b values
  const int = parseInt(hex, 16)
  const r = (int >> 16) & 255
  const g = (int >> 8) & 255
  const b = int & 255

  return { r, g, b }
}

/**
 * Converts RGB to hex.
 *
 * @param rgb
 */
export function rgbToHex(rgb: Rgb): string {
  const r = rgb.r.toString(16).padStart(2, '0')
  const g = rgb.g.toString(16).padStart(2, '0')
  const b = rgb.b.toString(16).padStart(2, '0')

  return `#${r}${g}${b}`
}

/**
 * Calculates the relative luminance of a color according to WCAG 2.0 guidelines.
 * Returns a value between 0 (darkest) and 1 (lightest).
 *
 * @param color
 */
export function getLuminance(color: string | Rgb): number {
  const rgb = typeof color === 'string' ? hexToRgb(color) : color

  const rsRGB = rgb.r / 255
  const gsRGB = rgb.g / 255
  const bsRGB = rgb.b / 255

  const r =
    rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4)
  const g =
    gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4)
  const b =
    bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4)

  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * Creates a more vivid version of a dark color by adjusting in LAB color space.
 * This is better for very dark, saturated colors like #990000.
 *
 * @param color
 * @param targetLuminance The target luminance (0-1)
 */
export function vivify(color: string | Rgb, targetLuminance = 0.15): string {
  const rgb = typeof color === 'string' ? hexToRgb(color) : color
  const currentLuminance = getLuminance(rgb)

  if (currentLuminance >= targetLuminance) {
    return rgbToHex(rgb)
  }

  // Calculate how much we need to scale the color
  // This maintains the color ratios while increasing brightness
  const scale = Math.sqrt(targetLuminance / currentLuminance) * 1.2

  const newRgb: Rgb = {
    r: Math.min(255, Math.round(rgb.r * scale)),
    g: Math.min(255, Math.round(rgb.g * scale)),
    b: Math.min(255, Math.round(rgb.b * scale)),
  }

  return rgbToHex(newRgb)
}
